import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { motion } from "framer-motion";
import {
  Languages,
  BookOpen,
  GraduationCap,
  // BookMarked,
  Check,
  RotateCcw,
  Globe,
  X,
  Plus,
  Sparkles,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { vocabularyLevelChecker } from "@/lib/vocabularyLevel";
import { vocabularyLevelManager } from "@/lib/vocabularyLevel/manager";
import { UserSettings } from "@/contexts/user-settings-context";
import { useI18n } from "@/lib/i18n";
import { supabase } from "@/core/supabase";
import { useUser } from "@/lib/context/user-context";
import { hasEpisodeFeatureAccess } from "@/lib/feature-access";
import { resolveDictionaryDisplayName } from "@/lib/vocabulary/dictionary-display";

const PANEL_WRAPPER_CLASS =
  "pointer-events-none fixed right-[6px] md:right-4 sm:right-12 top-[416px] md:top-[590px] sm:top-[500px] z-40";
const PANEL_INNER_CLASS =
  "pointer-events-auto flex flex-col gap-2 rounded-3xl border border-border/60 sm:bg-background/80 bg-background/50 sm:p-2 p-1 shadow-2xl sm:supports-[backdrop-filter]:backdrop-blur-xl supports-[backdrop-filter]:backdrop-blur-none dark:border-slate-800/60 dark:bg-slate-900/80";
const COLLAPSE_BUTTON_WRAPPER_CLASS =
  "fixed right-2 md:right-6 sm:right-12 top-[420px] md:top-[600px] sm:top-[500px] z-40";
const COLLAPSE_BUTTON_CLASS =
  "relative overflow-hidden h-10 w-10 rounded-2xl border border-border/70 bg-background/85 text-foreground shadow-xl transition-all duration-200 hover:-translate-y-0.5 hover:bg-background focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 supports-[backdrop-filter]:backdrop-blur-xl dark:border-slate-800/70 dark:bg-slate-900/85";
const COLLAPSE_BUTTON_ACTIVE_CLASS =
  "border text-foreground bg-background shadow-[0_12px_24px_-12px_rgba(59,130,246,0.45)] hover:bg-primary focus-visible:ring-primary/60 dark:border-primary/60 dark:bg-primary/90";
const ICON_BUTTON_BASE_CLASS =
  "group relative overflow-hidden h-9 w-9 sm:h-11 sm:w-11 rounded-2xl border border-border/70 bg-background/80 text-muted-foreground shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 supports-[backdrop-filter]:backdrop-blur-md dark:border-slate-800/70 dark:bg-slate-900/70 dark:text-slate-200";
const ICON_BUTTON_ACTIVE_CLASS =
  "border-primary/80 bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 hover:text-primary-foreground";
const ICON_BUTTON_SUBTLE_CLASS =
  "hover:bg-background/90 dark:hover:bg-slate-900/80";
const ICON_BUTTON_DANGER_CLASS =
  "border-red-500/60 bg-red-500/10 text-red-500 shadow-sm hover:bg-red-500/15 hover:text-red-600";
const TOOLTIP_BASE_CLASS =
  "rounded-xl border border-border/60 bg-background/95 px-3 py-2 text-xs shadow-lg supports-[backdrop-filter]:backdrop-blur-md dark:border-slate-800/70 dark:bg-slate-900/90";
const TOOLTIP_WARNING_CLASS =
  "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-400/40 dark:bg-orange-500/10 dark:text-orange-200";
const TOOLTIP_DANGER_CLASS =
  "border-red-200 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200";

interface FloatingActionButtonsProps {
  showTranslation: boolean;
  setShowTranslation: (show: boolean) => void;
  wordHighlightEnabled: boolean;
  setWordHighlightEnabled: (enabled: boolean) => void;
  wordHighlightLoading: boolean;
  setWordHighlightLoading: (loading: boolean) => void;
  practiceMode: boolean;
  setPracticeMode: (mode: boolean) => void;
  practiceModeType: "vocabulary" | "toefl" | "shadowing";
  setPracticeModeType: (type: "vocabulary" | "toefl" | "shadowing") => void;
  vocabWarmUpOpen: boolean;
  setVocabWarmUpOpen: (open: boolean) => void;
  vocabulary: any;
  setIsWordCheckerReady: (ready: boolean) => void;
  userSettings?: UserSettings;
  transcript?: any[];
  episodeId?: string;
  onTranscriptUpdate?: (transcript: any[]) => void;
  onWordHighlightEnabled?: () => void;
  onVocabularyUpdate?: (updatedVocabulary: any) => void;
}

export const FloatingActionButtons: React.FC<FloatingActionButtonsProps> = ({
  showTranslation,
  setShowTranslation,
  wordHighlightEnabled,
  setWordHighlightEnabled,
  wordHighlightLoading,
  setWordHighlightLoading,
  practiceMode,
  setPracticeMode,
  practiceModeType,
  setPracticeModeType,
  // vocabWarmUpOpen,
  // setVocabWarmUpOpen,
  vocabulary,
  setIsWordCheckerReady,
  userSettings,
  transcript = [],
  episodeId,
  onTranscriptUpdate,
  onWordHighlightEnabled,
  onVocabularyUpdate,
}) => {
  const [hasRevealedWords, setHasRevealedWords] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isGeneratingChosenExplanation, setIsGeneratingChosenExplanation] =
    useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { t } = useI18n();
  const { user } = useUser();
  const hasChosenExplanationAccess = hasEpisodeFeatureAccess(user);
  const noDictionaryLabel = t("episode.floatingActions.practice.noDictionary");

  const chosenExplanationButtonLabel = t(
    "episode.floatingActions.buttons.generateChosenExplanation",
    "Generate AI explanations"
  );
  const chosenExplanationButtonGeneratingLabel = t(
    "episode.floatingActions.buttons.generatingChosenExplanation",
    "Generating explanations"
  );
  const chosenExplanationTooltipLabel = t(
    "episode.floatingActions.tooltips.generateChosenExplanation",
    "Generate short explanations for each vocabulary term."
  );
  const chosenExplanationTooltipGeneratingLabel = t(
    "episode.floatingActions.tooltips.generatingChosenExplanation",
    "Generating explanations..."
  );
  const chosenExplanationSuccessMessage = t(
    "episode.floatingActions.success.chosenExplanationGenerated",
    "Explanations generated successfully!"
  );
  const chosenExplanationMissingDataMessage = t(
    "episode.floatingActions.errors.chosenExplanationMissing",
    "Unable to generate explanations: vocabulary is not ready."
  );
  const chosenExplanationUnauthorizedMessage = t(
    "episode.floatingActions.errors.chosenExplanationUnauthorized",
    "Please sign in to generate explanations."
  );
  const chosenExplanationErrorMessage = t(
    "episode.floatingActions.errors.chosenExplanationFailed",
    "Failed to generate explanations"
  );
  const translationUnavailableTooltip = t(
    "episode.floatingActions.tooltips.translationUnavailable",
    "Generate a translation to enable this"
  );

  // Get user's target language code
  const userTargetLanguageCode = useMemo(() => {
    const lang =
      userSettings?.languageSetting?.split("-")[0]?.toLowerCase() || "zh";
    return lang;
  }, [userSettings?.languageSetting]);

  // Check if user's language setting is Chinese
  const isChineseLanguage = useMemo(() => {
    const lang =
      userSettings?.languageSetting?.split("-")[0]?.toLowerCase() || "zh";
    return lang === "zh" || lang === "chinese";
  }, [userSettings?.languageSetting]);

  // Helper function to check if translation exists for user's language
  const hasTranslationForLanguage = useCallback(
    (translation: any, targetLang: string): boolean => {
      if (!translation) return false;

      // If it's a string, assume it's Chinese (legacy format)
      if (typeof translation === "string") {
        return targetLang === "zh";
      }

      // If it's an object, check if it has the target language key
      if (typeof translation === "object" && translation !== null) {
        return Boolean(
          translation[targetLang] && translation[targetLang].trim().length > 0
        );
      }

      return false;
    },
    []
  );

  // Check if transcript has translations
  const hasTranslations = useMemo(() => {
    // If no transcript, no translations available
    if (!transcript || transcript.length === 0) return false;

    return transcript.some((segment) => {
      // Check if segment has translation for user's language
      if (
        hasTranslationForLanguage(segment.translation, userTargetLanguageCode)
      )
        return true;

      // Check if sentences have translations for user's language
      if (
        segment.sentences &&
        segment.sentences.some((s: any) =>
          hasTranslationForLanguage(s.translation, userTargetLanguageCode)
        )
      )
        return true;

      // Check if chunks have translations for user's language
      if (
        segment.chunks &&
        segment.chunks.some((c: any) =>
          hasTranslationForLanguage(c.translation, userTargetLanguageCode)
        )
      )
        return true;

      return false;
    });
  }, [transcript, userTargetLanguageCode, hasTranslationForLanguage]);

  useEffect(() => {
    if (!hasTranslations && showTranslation) {
      setShowTranslation(false);
    }
  }, [hasTranslations, setShowTranslation, showTranslation]);

  const transcriptVocabularyWords = useMemo(() => {
    if (!transcript || transcript.length === 0) {
      return [] as string[];
    }

    const wordRegex = /\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/g;
    const words = new Set<string>();

    const collectFromText = (text?: string) => {
      if (!text) return;
      const matches = text.match(wordRegex);
      matches?.forEach((match) => {
        if (match.length > 1) {
          words.add(match.toLowerCase());
        }
      });
    };

    transcript.forEach((segment) => {
      collectFromText(segment?.text);

      segment?.sentences?.forEach((sentence: any) => {
        collectFromText(sentence?.text);
      });

      segment?.chunks?.forEach((chunk: any) => {
        collectFromText(chunk?.text);

        chunk?.sentences?.forEach((sentence: any) => {
          collectFromText(sentence?.text);
        });
      });
    });

    return Array.from(words);
  }, [transcript]);

  const shouldShowChosenExplanationButton = useMemo(() => {
    if (!hasChosenExplanationAccess) {
      return false;
    }

    if (!vocabulary?.scenarios || vocabulary.scenarios.length === 0) {
      return false;
    }

    return !vocabulary.scenarios.some((scenario: any) =>
      scenario?.terms?.some((term: any) => Boolean(term?.chosen_explanation))
    );
  }, [hasChosenExplanationAccess, vocabulary]);

  // Handle translation generation
  const handleTranslate = async () => {
    if (!episodeId || !transcript || transcript.length === 0) {
      alert(t("episode.floatingActions.alerts.translateMissingData"));
      return;
    }

    setIsTranslating(true);
    try {
      // Get session token for authentication (so API can read user's languageSetting)
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      // Add authorization header if session exists
      if (session?.access_token) {
        headers["Authorization"] = `Bearer ${session.access_token}`;
      }

      // Pass targetLanguage if userSettings is available (as fallback if no session)
      const requestBody: any = { transcript };
      if (userSettings?.languageSetting && !session?.access_token) {
        requestBody.targetLanguage = userSettings.languageSetting;
      }

      const response = await fetch(`/api/episodes/${episodeId}/translate`, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(
          `${t("episode.floatingActions.errors.translateRequestFailed")}: ${
            response.status
          }`
        );
      }

      const result = await response.json();
      if (result.success && result.translatedTranscript) {
        // Update transcript with translations
        onTranscriptUpdate?.(result.translatedTranscript);
        alert(t("episode.floatingActions.success.translateGenerated"));
      } else {
        throw new Error(
          t("episode.floatingActions.errors.translateResponseInvalid")
        );
      }
    } catch (error) {
      console.error(
        t("episode.floatingActions.errors.translateConsoleError"),
        error
      );
      alert(t("episode.floatingActions.errors.translateFailed"));
    } finally {
      setIsTranslating(false);
    }
  };

  const handleGenerateChosenExplanation = useCallback(async () => {
    if (!hasChosenExplanationAccess) {
      return;
    }

    if (isGeneratingChosenExplanation) {
      return;
    }

    if (
      !episodeId ||
      !vocabulary?.scenarios ||
      vocabulary.scenarios.length === 0
    ) {
      alert(chosenExplanationMissingDataMessage);
      return;
    }

    setIsGeneratingChosenExplanation(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        alert(chosenExplanationUnauthorizedMessage);
        return;
      }

      const response = await fetch(
        `/api/episodes/${episodeId}/vocabulary/chosen-explanation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const result = await response.json();

      if (result?.success && result?.vocabulary) {
        onVocabularyUpdate?.(result.vocabulary);
        alert(chosenExplanationSuccessMessage);
      } else {
        throw new Error("Invalid response payload");
      }
    } catch (error) {
      console.error("Failed to generate chosen explanations:", error);
      alert(chosenExplanationErrorMessage);
    } finally {
      setIsGeneratingChosenExplanation(false);
    }
  }, [
    chosenExplanationErrorMessage,
    chosenExplanationMissingDataMessage,
    chosenExplanationSuccessMessage,
    chosenExplanationUnauthorizedMessage,
    episodeId,
    hasChosenExplanationAccess,
    isGeneratingChosenExplanation,
    onVocabularyUpdate,
    vocabulary,
  ]);

  // Helper function to get user's dictionary settings - No default dictionary
  const getUserDictionarySettings = useCallback(() => {
    const dictionaryFile = userSettings?.dictionarySetting;

    // No default - user must explicitly set a dictionary
    if (!dictionaryFile) {
      return { dictionaryType: null, dictionaryFile: null };
    }

    let dictionaryType = userSettings?.vocabularyDictionary;

    // 如果设置了具体的词典文件但没有设置词典类型，根据文件名推断类型
    if (!dictionaryType && dictionaryFile) {
      const lowerFileName = dictionaryFile.toLowerCase();

      // CET 词库
      if (
        lowerFileName.includes("cet4") ||
        lowerFileName.includes("cet-4") ||
        lowerFileName.includes("xinghuoqiaoji_4") ||
        lowerFileName.includes("dancidejianfa_4")
      ) {
        dictionaryType = "CET4";
      } else if (
        lowerFileName.includes("cet6") ||
        lowerFileName.includes("cet-6") ||
        lowerFileName.includes("xinghuoqiaoji_6") ||
        lowerFileName.includes("dancidejianfa_6")
      ) {
        dictionaryType = "CET6";
      }
      // TEM 专四专八
      else if (
        lowerFileName.includes("level4luan") ||
        lowerFileName.includes("tem4") ||
        lowerFileName.includes("te4")
      ) {
        dictionaryType = "TEM4";
      } else if (
        lowerFileName.includes("level8luan") ||
        lowerFileName.includes("tem8") ||
        lowerFileName.includes("te8")
      ) {
        dictionaryType = "TEM8";
      }
      // 考研词库
      else if (
        lowerFileName.includes("kaoyan") ||
        lowerFileName.includes("dancidemi") ||
        lowerFileName.includes("hongbao") ||
        lowerFileName.includes("english_ii") ||
        lowerFileName.includes("shangguo")
      ) {
        dictionaryType = "KaoYan";
      }
      // TOEFL 词库
      else if (
        lowerFileName.includes("toefl") ||
        lowerFileName.includes("_t.json") ||
        lowerFileName.includes("zhanghongyan")
      ) {
        dictionaryType = "TOEFL";
      }
      // IELTS 词库
      else if (
        lowerFileName.includes("ielts") ||
        lowerFileName.includes("heqiong") ||
        lowerFileName.includes("wangu807") ||
        lowerFileName.includes("duckcircle") ||
        lowerFileName.includes("xdf_7000")
      ) {
        dictionaryType = "IELTS";
      }
      // GRE 词库
      else if (
        lowerFileName.includes("gre") ||
        lowerFileName.includes("zaiyouniming")
      ) {
        dictionaryType = "GRE";
      }
      // GMAT 词库
      else if (lowerFileName.includes("gmat")) {
        dictionaryType = "GMAT";
      }
      // SAT 词库
      else if (
        lowerFileName.includes("sat") ||
        lowerFileName.includes("saten")
      ) {
        dictionaryType = "SAT";
      }
      // TOEIC 词库
      else if (lowerFileName.includes("toeic")) {
        dictionaryType = "TOEIC";
      }
      // BEC 词库
      else if (lowerFileName.includes("bec")) {
        dictionaryType = "BEC";
      }
      // PTE 词库
      else if (
        lowerFileName.includes("pte") ||
        lowerFileName.includes("wfd") ||
        lowerFileName.includes("fib") ||
        lowerFileName.includes("apeuni")
      ) {
        dictionaryType = "PTE";
      }
      // KET 词库
      else if (lowerFileName.includes("ket")) {
        dictionaryType = "KET";
      }
      // PET/PETS 词库
      else if (
        lowerFileName.includes("pets") ||
        lowerFileName.includes("pet-") ||
        lowerFileName.includes("pet2024")
      ) {
        dictionaryType = "PET";
      }
      // FCE 词库
      else if (lowerFileName.includes("fce")) {
        dictionaryType = "FCE";
      }
      // CEFR 词库
      else if (
        lowerFileName.includes("cefr") ||
        lowerFileName.includes("duolingo")
      ) {
        dictionaryType = "CEFR";
      }
      // Oxford 词库
      else if (
        lowerFileName.includes("oxford") ||
        lowerFileName.includes("macmillan")
      ) {
        dictionaryType = "Oxford";
      }
      // 专业英语词库
      else if (
        lowerFileName.includes("arch") ||
        lowerFileName.includes("it") ||
        lowerFileName.includes("computer")
      ) {
        dictionaryType = "Professional";
      }
      // 自考词库
      else if (
        lowerFileName.includes("self-study") ||
        lowerFileName.includes("adult-self") ||
        lowerFileName.includes("zhuan-cha-ben") ||
        lowerFileName.includes("xueshiyingyu")
      ) {
        dictionaryType = "SelfStudy";
      }
      // VOA 等其他词库
      else if (
        lowerFileName.includes("voa") ||
        lowerFileName.includes("coca") ||
        lowerFileName.includes("longman") ||
        lowerFileName.includes("essential") ||
        lowerFileName.includes("top") ||
        lowerFileName.includes("suffix") ||
        lowerFileName.includes("roots") ||
        lowerFileName.includes("merriam")
      ) {
        dictionaryType = "Other";
      }
      // 默认fallback
      else {
        // 根据文件扩展名和常见模式进行最后判断
        if (
          lowerFileName.endsWith(".json") &&
          (lowerFileName.includes("words") ||
            lowerFileName.includes("vocab") ||
            lowerFileName.includes("dictionary"))
        ) {
          dictionaryType = "TOEFL"; // 默认TOEFL
          console.log(
            "⚠️ Unknown dictionary file format, defaulting to TOEFL:",
            dictionaryFile
          );
        } else {
          dictionaryType = "Unknown";
          console.log(
            "❌ Unrecognized dictionary file format:",
            dictionaryFile
          );
        }
      }

      console.log("🔍 Dictionary type inferred:", {
        dictionaryFile,
        dictionaryType,
      });
    }

    return { dictionaryType, dictionaryFile };
  }, [userSettings]);

  // Helper function to get display name for the dictionary
  const getDictionaryDisplayName = useCallback(() => {
    const dictionaryFile = userSettings?.dictionarySetting;

    if (!dictionaryFile) return noDictionaryLabel;

    const resolvedName = resolveDictionaryDisplayName(dictionaryFile);
    return resolvedName || noDictionaryLabel;
  }, [noDictionaryLabel, userSettings?.dictionarySetting]);

  // Check if current episode has vocabulary progress in localStorage
  const checkRevealedWords = () => {
    // If no episodeId, can't check specific episode progress
    if (!episodeId) return false;

    // Check only current episode's progress keys
    const currentEpisodeKeys = [
      `revealedVocabWords-${episodeId}`, // Old format
      `vocabularyProgress-${episodeId}`, // New format
    ];

    // Check if current episode has any progress
    for (const key of currentEpisodeKeys) {
      const savedData = localStorage.getItem(key);
      if (savedData) {
        try {
          const progressData = JSON.parse(savedData);

          // Check based on data format
          if (key.startsWith("vocabularyProgress-")) {
            // New format: check hiddenWords, completedWords, or visitedChunks
            if (
              (progressData.hiddenWords &&
                Object.keys(progressData.hiddenWords).length > 0) ||
              (progressData.completedWords &&
                Object.keys(progressData.completedWords).length > 0) ||
              (progressData.visitedChunks &&
                progressData.visitedChunks.length > 0)
            ) {
              return true;
            }
          } else {
            // Old format: check if object has any keys
            if (Object.keys(progressData).length > 0) {
              return true;
            }
          }
        } catch (error) {
          console.error("Error parsing progress data:", error);
        }
      }
    }

    return false;
  };

  // Clear revealed words for current episode
  const clearRevealedWords = () => {
    setShowClearDialog(true);
  };

  // Handle confirmed clear action
  const handleConfirmClear = () => {
    // If no episodeId, can't clear specific episode progress
    if (!episodeId) {
      console.warn("No episodeId provided, cannot clear progress");
      setShowClearDialog(false);
      return;
    }

    // Remove only current episode's localStorage keys
    const currentEpisodeKeys = [
      `revealedVocabWords-${episodeId}`, // Old format
      `vocabularyProgress-${episodeId}`, // New format
    ];

    let removedCount = 0;
    currentEpisodeKeys.forEach((key) => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        removedCount++;
      }
    });

    setHasRevealedWords(false);
    setShowClearDialog(false);

    // 发送全局事件通知所有GroupedTranscriptEntry组件重置状态
    window.dispatchEvent(
      new CustomEvent("clearVocabularyProgress", {
        detail: { removedCount, episodeId },
      })
    );
  };

  // Also check when practice mode changes or when localStorage might have been updated
  useEffect(() => {
    const handleStorageChange = () => {
      setHasRevealedWords(checkRevealedWords());
    };

    // Initial check when component mounts or episodeId changes
    handleStorageChange();

    // Listen for storage changes (when words are revealed)
    window.addEventListener("storage", handleStorageChange);

    // Also check periodically (for updates within the same tab)
    const interval = setInterval(handleStorageChange, 5000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [episodeId, checkRevealedWords]); // Add episodeId as dependency

  useEffect(() => {
    if (!isCollapsed) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setIsCollapsed(true);
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isCollapsed]);

  const dictionaryMissing =
    !userSettings?.dictionarySetting && !vocabularyLevelManager.isInitialized();
  const practiceLocked =
    !vocabulary?.scenarios && !userSettings?.dictionarySetting;
  const practiceModeLabels = {
    vocabulary: t("episode.floatingActions.practiceModes.vocabulary"),
    toefl: t("episode.floatingActions.practiceModes.toefl"),
    shadowing: t("episode.floatingActions.practiceModes.shadowing"),
  } as const;
  const activePracticeLabel =
    practiceModeLabels[practiceModeType] ?? practiceModeType.toUpperCase();
  const practiceModeActiveDescription = t(
    "episode.floatingActions.tooltips.practiceActiveDescription"
  ).replace("{mode}", activePracticeLabel);
  const dictionarySpellingSuffix = t(
    "episode.floatingActions.practice.spellingSuffix"
  );
  const dictionaryDisplayName = getDictionaryDisplayName();
  const showDictionaryDisplayName =
    !dictionaryMissing &&
    dictionaryDisplayName &&
    dictionaryDisplayName !== noDictionaryLabel;
  const wordHighlightDictionaryLabel = showDictionaryDisplayName
    ? t(
        "episode.floatingActions.tooltips.wordHighlightDictionaryLabel",
        "Dictionary: {dictionary}"
      ).replace("{dictionary}", dictionaryDisplayName)
    : null;
  const wordHighlightButtonBaseLabel = wordHighlightEnabled
    ? t("episode.floatingActions.buttons.disableWordHighlight")
    : t("episode.floatingActions.buttons.enableWordHighlight");
  const wordHighlightTooltipBaseLabel = wordHighlightEnabled
    ? t("episode.floatingActions.tooltips.disableWordHighlight")
    : t("episode.floatingActions.tooltips.enableWordHighlight");
  const wordHighlightAriaLabel = showDictionaryDisplayName
    ? `${wordHighlightButtonBaseLabel} (${dictionaryDisplayName})`
    : wordHighlightButtonBaseLabel;

  const collapsedButtonState = useMemo(() => {
    const spinner = (
      <svg
        className="h-5 w-5 animate-spin"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    if (isGeneratingChosenExplanation) {
      return {
        icon: spinner,
        isActive: true,
      } as const;
    }

    if (isTranslating) {
      return {
        icon: spinner,
        isActive: true,
      } as const;
    }

    if (wordHighlightLoading) {
      return {
        icon: spinner,
        isActive: true,
      } as const;
    }

    if (showTranslation) {
      return {
        icon: <Languages className="h-5 w-5" />,
        isActive: true,
      } as const;
    }

    if (wordHighlightEnabled) {
      return {
        icon: <BookOpen className="h-5 w-5" />,
        isActive: true,
      } as const;
    }

    if (practiceMode) {
      return {
        icon: <GraduationCap className="h-5 w-5" />,
        isActive: true,
      } as const;
    }

    return {
      icon: <Plus className="h-5 w-5" />,
      isActive: false,
    } as const;
  }, [
    isTranslating,
    practiceMode,
    showTranslation,
    wordHighlightEnabled,
    wordHighlightLoading,
    isGeneratingChosenExplanation,
  ]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const handlePracticeModeChange = async (
    type: "vocabulary" | "toefl" | "shadowing"
  ) => {
    // For vocabulary mode, check if vocabulary is generated
    if (type === "vocabulary") {
      if (!vocabulary || !vocabulary.scenarios) {
        alert(t("episode.floatingActions.errors.generateVocabularyFirst"));
        return;
      }
    }

    // For TOEFL mode, ensure user's selected dictionary is initialized
    if (type === "toefl") {
      setWordHighlightLoading(true);
      try {
        const { dictionaryType, dictionaryFile } = getUserDictionarySettings();

        // If user hasn't selected a dictionary, use default TOEFL
        const finalDictionaryType = dictionaryType || "TOEFL";
        const finalDictionaryFile = dictionaryFile || "Level8luan_2_T.json";

        console.log("🔍 TOEFL mode initialization:", {
          originalDictionaryType: dictionaryType,
          originalDictionaryFile: dictionaryFile,
          finalDictionaryType,
          finalDictionaryFile,
        });

        await vocabularyLevelManager.ensureInitialized(
          finalDictionaryType,
          finalDictionaryFile
        );

        if (transcriptVocabularyWords.length > 0) {
          try {
            await (vocabularyLevelChecker as any).preloadCommonWords?.(
              transcriptVocabularyWords
            );
          } catch (preloadError) {
            console.warn("Preloading vocabulary words failed:", preloadError);
          }
        }

        setIsWordCheckerReady(true);
      } catch (error) {
        console.error(
          t("episode.floatingActions.errors.dictionaryInitConsole"),
          error
        );
        alert(t("episode.floatingActions.errors.dictionaryLoadFailed"));
        setWordHighlightLoading(false);
        return;
      } finally {
        setWordHighlightLoading(false);
      }
    }

    // For shadowing mode, no additional checks needed
    if (type === "shadowing") {
      // Shadowing mode doesn't require vocabulary or dictionary
    }

    setPracticeModeType(type);
    const newPracticeMode = !practiceMode;
    setPracticeMode(newPracticeMode);
  };

  const handleWordHighlightToggle = useCallback(async () => {
    if (wordHighlightLoading) {
      return;
    }

    if (wordHighlightEnabled) {
      setWordHighlightEnabled(false);
      return;
    }

    if (dictionaryMissing) {
      alert(t("episode.floatingActions.errors.dictionaryNotSelected"));
      return;
    }

    const { dictionaryType, dictionaryFile } = getUserDictionarySettings();

    if (!dictionaryFile || !dictionaryType) {
      alert(t("episode.floatingActions.errors.dictionaryNotSelected"));
      return;
    }

    setWordHighlightLoading(true);

    try {
      const isManagerInitialized = vocabularyLevelManager.isInitialized();
      const currentDictionaryFile =
        vocabularyLevelManager.getCurrentDictionaryFile();

      if (!isManagerInitialized || currentDictionaryFile !== dictionaryFile) {
        await vocabularyLevelManager.ensureInitialized(
          dictionaryType,
          dictionaryFile
        );
      }

      if (!vocabularyLevelManager.isInitialized()) {
        throw new Error(
          t("episode.floatingActions.errors.dictionaryInitFailed")
        );
      }

      if (transcriptVocabularyWords.length > 0) {
        try {
          await (vocabularyLevelChecker as any).preloadCommonWords?.(
            transcriptVocabularyWords
          );
        } catch (preloadError) {
          console.warn("Preloading vocabulary words failed:", preloadError);
        }
      }

      setWordHighlightEnabled(true);
      setIsWordCheckerReady(true);
      onWordHighlightEnabled?.();
    } catch (error) {
      console.error(
        t("episode.floatingActions.errors.dictionaryInitConsole"),
        error
      );
      alert(t("episode.floatingActions.errors.dictionaryInitUnable"));
      setIsWordCheckerReady(false);
    } finally {
      setWordHighlightLoading(false);
    }
  }, [
    dictionaryMissing,
    getUserDictionarySettings,
    onWordHighlightEnabled,
    setIsWordCheckerReady,
    setWordHighlightEnabled,
    setWordHighlightLoading,
    transcriptVocabularyWords,
    t,
    wordHighlightEnabled,
    wordHighlightLoading,
  ]);

  const collapsedContent = (
    <TooltipProvider delayDuration={120}>
      <div className={COLLAPSE_BUTTON_WRAPPER_CLASS}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("episode.floatingActions.buttons.open")}
              aria-expanded={!isCollapsed}
              className={cn(
                COLLAPSE_BUTTON_CLASS,
                collapsedButtonState.isActive && COLLAPSE_BUTTON_ACTIVE_CLASS
              )}
              onClick={() => setIsCollapsed(false)}
            >
              {/* <ActiveButtonGlow active={collapsedButtonState.isActive} borderRadius="9999px" /> */}
              <span className="relative z-[1] flex items-center justify-center">
                {collapsedButtonState.icon}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className={TOOLTIP_BASE_CLASS}>
            <p>{t("episode.floatingActions.tooltips.open")}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );

  const expandedContent = (
    <TooltipProvider delayDuration={120}>
      <div className={PANEL_WRAPPER_CLASS}>
        <div className={PANEL_INNER_CLASS}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("episode.floatingActions.buttons.collapse")}
                className={cn(ICON_BUTTON_BASE_CLASS, ICON_BUTTON_SUBTLE_CLASS)}
                onClick={() => setIsCollapsed(true)}
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className={TOOLTIP_BASE_CLASS}>
              <p>{t("episode.floatingActions.tooltips.collapse")}</p>
            </TooltipContent>
          </Tooltip>

          {/* Only show translation toggle button when translations are available */}
          {hasTranslations && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-pressed={showTranslation}
                  aria-label={
                    showTranslation
                      ? t("episode.floatingActions.buttons.hideTranslation")
                      : t("episode.floatingActions.buttons.showTranslation")
                  }
                  className={cn(
                    ICON_BUTTON_BASE_CLASS,
                    ICON_BUTTON_SUBTLE_CLASS,
                    showTranslation && ICON_BUTTON_ACTIVE_CLASS
                  )}
                  onClick={() => setShowTranslation(!showTranslation)}
                >
                  {/* <ActiveButtonGlow active={showTranslation} /> */}
                  <span className="relative z-[1] flex items-center justify-center">
                    <Languages className="h-4 w-4" />
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" className={TOOLTIP_BASE_CLASS}>
                <p>
                  {showTranslation
                    ? t("episode.floatingActions.tooltips.hideTranslation")
                    : t("episode.floatingActions.tooltips.showTranslation")}
                </p>
              </TooltipContent>
            </Tooltip>
          )}

          {shouldShowChosenExplanationButton && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={
                    isGeneratingChosenExplanation
                      ? chosenExplanationButtonGeneratingLabel
                      : chosenExplanationButtonLabel
                  }
                  aria-busy={isGeneratingChosenExplanation}
                  className={cn(
                    ICON_BUTTON_BASE_CLASS,
                    ICON_BUTTON_SUBTLE_CLASS
                  )}
                  onClick={handleGenerateChosenExplanation}
                  disabled={isGeneratingChosenExplanation}
                >
                  <span className="relative z-[1] flex items-center justify-center">
                    {isGeneratingChosenExplanation ? (
                      <svg
                        className="h-4 w-4 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" className={TOOLTIP_BASE_CLASS}>
                <p>
                  {isGeneratingChosenExplanation
                    ? chosenExplanationTooltipGeneratingLabel
                    : chosenExplanationTooltipLabel}
                </p>
              </TooltipContent>
            </Tooltip>
          )}

          {!hasTranslations && episodeId && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={
                    isTranslating
                      ? t(
                          "episode.floatingActions.buttons.generatingTranslation"
                        )
                      : t("episode.floatingActions.buttons.generateTranslation")
                  }
                  aria-busy={isTranslating}
                  className={cn(
                    ICON_BUTTON_BASE_CLASS,
                    ICON_BUTTON_SUBTLE_CLASS
                  )}
                  onClick={handleTranslate}
                  disabled={isTranslating}
                >
                  {isTranslating ? (
                    <svg
                      className="h-4 w-4 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <Globe className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" className={TOOLTIP_BASE_CLASS}>
                <p>
                  {isTranslating
                    ? t(
                        "episode.floatingActions.tooltips.generatingTranslation"
                      )
                    : t("episode.floatingActions.tooltips.generateTranslation")}
                </p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Only show Word Highlight button if user's language is Chinese */}
          {isChineseLanguage && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-pressed={wordHighlightEnabled}
                  aria-label={wordHighlightAriaLabel}
                  className={cn(
                    ICON_BUTTON_BASE_CLASS,
                    ICON_BUTTON_SUBTLE_CLASS,
                    wordHighlightEnabled && ICON_BUTTON_ACTIVE_CLASS,
                    dictionaryMissing && "cursor-not-allowed"
                  )}
                  onClick={handleWordHighlightToggle}
                  disabled={wordHighlightLoading || dictionaryMissing}
                >
                  {/* <ActiveButtonGlow active={wordHighlightEnabled || wordHighlightLoading} /> */}
                  <span className="relative z-[1] flex items-center justify-center">
                    {wordHighlightLoading ? (
                      <svg
                        className="h-4 w-4 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <BookOpen className="h-4 w-4" />
                    )}
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                className={cn(
                  TOOLTIP_BASE_CLASS,
                  dictionaryMissing && TOOLTIP_WARNING_CLASS
                )}
              >
                {dictionaryMissing ? (
                  <div className="max-w-[200px] space-y-1">
                    <p className="font-semibold">
                      {t(
                        "episode.floatingActions.tooltips.dictionaryRequiredTitle"
                      )}
                    </p>
                    <p className="text-xs text-orange-600 dark:text-orange-200/80">
                      {t(
                        "episode.floatingActions.tooltips.dictionaryRequiredDescription"
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="max-w-[220px] space-y-1">
                    <p className="font-semibold">
                      {wordHighlightTooltipBaseLabel}
                    </p>
                    {wordHighlightDictionaryLabel && (
                      <p className="text-xs text-muted-foreground">
                        {wordHighlightDictionaryLabel}
                      </p>
                    )}
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          )}

          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-haspopup="menu"
                    aria-pressed={practiceMode}
                    aria-label={
                      practiceMode
                        ? t(
                            "episode.floatingActions.buttons.adjustPracticeMode"
                          )
                        : t(
                            "episode.floatingActions.buttons.enablePracticeMode"
                          )
                    }
                    className={cn(
                      ICON_BUTTON_BASE_CLASS,
                      ICON_BUTTON_SUBTLE_CLASS,
                      practiceMode && ICON_BUTTON_ACTIVE_CLASS,
                      practiceLocked && "opacity-60"
                    )}
                  >
                    {/* <ActiveButtonGlow active={practiceMode} /> */}
                    <span className="relative z-[1] flex items-center justify-center">
                      <GraduationCap className="h-4 w-4" />
                    </span>
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                className={cn(
                  TOOLTIP_BASE_CLASS,
                  practiceLocked && TOOLTIP_WARNING_CLASS
                )}
              >
                <div className="max-w-[220px] space-y-1">
                  {practiceLocked ? (
                    <>
                      <p className="font-semibold">
                        {t(
                          "episode.floatingActions.tooltips.practiceSetupTitle"
                        )}
                      </p>
                      <p className="text-xs text-orange-600 dark:text-orange-200/80">
                        {t(
                          "episode.floatingActions.tooltips.practiceSetupDescription"
                        )}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold">
                        {practiceMode
                          ? t(
                              "episode.floatingActions.tooltips.practiceActiveTitle"
                            )
                          : t(
                              "episode.floatingActions.tooltips.practiceOpenTitle"
                            )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {practiceMode
                          ? practiceModeActiveDescription
                          : t(
                              "episode.floatingActions.tooltips.practiceOpenDescription"
                            )}
                      </p>
                    </>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent
              align="end"
              side="right"
              className="w-[180px] space-y-1 rounded-2xl border border-border/70 bg-background/95 p-2 shadow-2xl supports-[backdrop-filter]:backdrop-blur-md"
            >
              {/* Only show Word Highlight button if user's language is Chinese */}
              {isChineseLanguage && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-pressed={wordHighlightEnabled}
                      aria-label={wordHighlightAriaLabel}
                      className={cn(
                        ICON_BUTTON_BASE_CLASS,
                        ICON_BUTTON_SUBTLE_CLASS,
                        wordHighlightEnabled && ICON_BUTTON_ACTIVE_CLASS,
                        dictionaryMissing && "cursor-not-allowed"
                      )}
                      onClick={handleWordHighlightToggle}
                      disabled={wordHighlightLoading || dictionaryMissing}
                    >
                      {/* <ActiveButtonGlow active={wordHighlightEnabled || wordHighlightLoading} /> */}
                      <span className="relative z-[1] flex items-center justify-center">
                        {wordHighlightLoading ? (
                          <svg
                            className="h-4 w-4 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : (
                          <BookOpen className="h-4 w-4" />
                        )}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="left"
                    className={cn(
                      TOOLTIP_BASE_CLASS,
                      dictionaryMissing && TOOLTIP_WARNING_CLASS
                    )}
                  >
                    {dictionaryMissing ? (
                      <div className="max-w-[200px] space-y-1">
                        <p className="font-semibold">
                          {t(
                            "episode.floatingActions.tooltips.dictionaryRequiredTitle"
                          )}
                        </p>
                        <p className="text-xs text-orange-600 dark:text-orange-200/80">
                          {t(
                            "episode.floatingActions.tooltips.dictionaryRequiredDescription"
                          )}
                        </p>
                      </div>
                    ) : (
                      <div className="max-w-[220px] space-y-1">
                        <p className="font-semibold">
                          {wordHighlightTooltipBaseLabel}
                        </p>
                        {wordHighlightDictionaryLabel && (
                          <p className="text-xs text-muted-foreground">
                            {wordHighlightDictionaryLabel}
                          </p>
                        )}
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              )}
              <DropdownMenuItem
                onClick={() => handlePracticeModeChange("shadowing")}
                className={cn(
                  "flex items-center gap-2 text-xs sm:text-sm",
                  practiceMode &&
                    practiceModeType === "shadowing" &&
                    "bg-primary/10 text-foreground font-medium"
                )}
              >
                {practiceMode && practiceModeType === "shadowing" && (
                  <Check className="h-4 w-4" />
                )}
                <span>{practiceModeLabels.shadowing}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {hasRevealedWords && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t(
                    "episode.floatingActions.buttons.resetProgress"
                  )}
                  className={cn(
                    ICON_BUTTON_BASE_CLASS,
                    ICON_BUTTON_DANGER_CLASS
                  )}
                  onClick={clearRevealedWords}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                className={cn(TOOLTIP_BASE_CLASS, TOOLTIP_DANGER_CLASS)}
              >
                <div className="max-w-[200px] space-y-1">
                  <p className="font-semibold">
                    {t("episode.floatingActions.tooltips.resetProgressTitle")}
                  </p>
                  <p className="text-xs">
                    {t(
                      "episode.floatingActions.tooltips.resetProgressDescription"
                    )}
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("episode.floatingActions.buttons.backToTop")}
                className={cn(ICON_BUTTON_BASE_CLASS, ICON_BUTTON_SUBTLE_CLASS)}
                onClick={scrollToTop}
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.8648 8.84197C11.6759 9.04343 11.3595 9.05363 11.158 8.86477L7.5 5.43536L3.84197 8.86477C3.64052 9.05363 3.3241 9.04343 3.13523 8.84197C2.94637 8.64051 2.95657 8.32409 3.15803 8.13523L7.15803 4.38523C7.35036 4.20492 7.64964 4.20492 7.84197 4.38523L11.842 8.13523C12.0434 8.32409 12.0536 8.64051 11.8648 8.84197Z"
                    fill="currentColor"
                    fillRule="evenodd"
                    clipRule="evenodd"
                  />
                </svg>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className={TOOLTIP_BASE_CLASS}>
              <p>{t("episode.floatingActions.tooltips.backToTop")}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );

  return (
    <>
      {isCollapsed ? collapsedContent : expandedContent}

      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-red-500" />
              {t("episode.floatingActions.dialog.title")}
            </DialogTitle>
            <DialogDescription>
              {t("episode.floatingActions.dialog.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowClearDialog(false)}>
              {t("episode.floatingActions.dialog.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmClear}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("episode.floatingActions.dialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
